import { supabase } from '../supabase/config';
import L from 'leaflet';

// Set up real-time updates for event participants
export const setupEventRealtimeUpdates = (eventId) => {
  const supabaseClient = window.supabase || supabase;
  
  // Clean up any existing subscription
  if (window.currentEventSubscription) {
    supabaseClient.removeChannel(window.currentEventSubscription);
  }
  
  // Subscribe to changes on this specific event
  const channel = supabaseClient
    .channel(`event_${eventId}`)
    .on(
      'postgres_changes',
      {
        event: 'UPDATE',
        schema: 'public' || 'private',
        table: 'events',
        filter: `event_id=eq.${eventId}`
      },
      (payload) => {
        // Skip if we're manually handling this update (during join/leave)
        if (window.skipRealtimeUpdate === eventId) {
          return;
        }
        
        // Check if participants changed
        if (payload.new && payload.new.participants) {
          window.updateEventDetailsPanel(eventId);
        }
      }
    )
    .subscribe(() => {});
  
  // Store the channel so we can clean it up later
  window.currentEventSubscription = channel;
};

// Clean up realtime subscription when panel closes
export const cleanupEventRealtimeUpdates = () => {
  // Clear the event details interval
  if (window.eventDetailsInterval) {
    clearInterval(window.eventDetailsInterval);
    window.eventDetailsInterval = null;
  }
  
  // Clear all other intervals
  if (window.eventIntervals) {
    window.eventIntervals.forEach(interval => clearInterval(interval));
    window.eventIntervals = [];
  }
};

export const updateEventDetailsPanel = async (eventId) => {
  const supabaseClient = window.supabase || supabase;
  
  try {
    // Get updated event data
    const { data: updatedEvent, error } = await supabaseClient
      .from('events')
      .select('*')
      .eq('event_id', eventId)
      .single();
    
    if (error) {
      return;
    }
    
    // Format the event data
    const formattedEvent = {
      eventId: updatedEvent.event_id,
      title: updatedEvent.title,
      description: updatedEvent.description,
      placeId: updatedEvent.place_id,
      placeName: updatedEvent.place_name,
      placeAddress: updatedEvent.place_address,
      placePhotoUrl: updatedEvent.place_photo_url,
      latitude: updatedEvent.latitude,
      longitude: updatedEvent.longitude,
      startDateTime: updatedEvent.start_date_time,
      endDateTime: updatedEvent.end_date_time,
      peopleLimit: updatedEvent.people_limit,
      creatorId: updatedEvent.creator_id,
      createdBy: updatedEvent.created_by,
      participants: updatedEvent.participants,
      customLabel: updatedEvent.custom_label,
      requireApprove: updatedEvent.require_approve
    };
    
    // Fetch new participants data
    const participantIds = formattedEvent.participants || [formattedEvent.creatorId];
    const otherParticipantIds = participantIds.filter(id => id !== formattedEvent.creatorId);
    const newParticipantsData = [];
    
    // SIMPLE: Always show maximum 5 avatars
    const maxAvatars = 5;
    const hasJoiners = otherParticipantIds.length > 0;
    const participantCount = participantIds.length;
    const limitReached = formattedEvent.peopleLimit && participantCount >= formattedEvent.peopleLimit;
    
    const showMoreCounter = otherParticipantIds.length > maxAvatars;
    // Add hasEnded check
const now = new Date();
const endDate = formattedEvent.endDateTime ? new Date(formattedEvent.endDateTime) : null;
const hasEnded = endDate && now >= endDate;

const showWaitingCircle = otherParticipantIds.length <= maxAvatars && !limitReached;
const animateWaiting = showWaitingCircle && !hasEnded;
    
    const visibleCount = Math.min(otherParticipantIds.length, maxAvatars);
    
    for (let i = 0; i < visibleCount; i++) {
      const participantId = otherParticipantIds[i];
      try {
        const { data, error } = await supabaseClient
          .from('users')
          .select('id, name, profile_picture')
          .eq('id', participantId)
          .single();
        
        if (!error && data) {
          newParticipantsData.push(data);
        }
      } catch (err) {
        console.error('Error fetching participant:', err);
      }
    }
    
    const remainingCount = otherParticipantIds.length - visibleCount;
    
    // Update the participants count
    const countElement = document.querySelector('#eventDetailsPanel [data-participants-count]');
    if (countElement) {
      const limit = formattedEvent.peopleLimit;
      if (limit === null || limit === undefined) {
        countElement.textContent = `${participantIds.length} / ∞`;
      } else {
        countElement.textContent = `${participantIds.length} / ${limit}`;
      }
    }
    
    // Update the participants row
    const participantsRow = document.querySelector('#eventDetailsPanel [data-participants-row]');
    if (participantsRow) {
      participantsRow.innerHTML = `
        ${newParticipantsData.map(participant => {
          const fallbackAvatar = `https://ui-avatars.com/api/?name=${encodeURIComponent(participant.name || 'User')}&background=e8eaed&color=5f6368&size=40`;
          return `
            <div 
              class="participant-avatar"
              onclick="window.showUserProfilePanel('${participant.id}')"
              style="flex-shrink: 0;"
            >
              <img 
                src="${participant.profile_picture || fallbackAvatar}"
                alt="${participant.name}"
                style="
                  width: 40px;
                  height: 40px;
                  border-radius: 50%;
                  object-fit: cover;
                  border: 2px solid #e8eaed;
                "
                onerror="this.src='${fallbackAvatar}'"
              />
            </div>
          `;
        }).join('')}
        
        ${showWaitingCircle ? `
          <div 
            style="
              width: 40px;
              height: 40px;
              border-radius: 50%;
              background: #dddddd;
              border: 2px solid #e8eaed;
              display: flex;
              align-items: center;
              justify-content: center;
              gap: 3px;
              cursor: default;
              flex-shrink: 0;
            "
            title="${hasEnded ? 'Event finished' : 'Waiting for joiners...'}"
          >
            <div ${animateWaiting ? 'class="wave-dot"' : ''} style="width: 4px; height: 4px; border-radius: 50%; background: #5f6368; flex-shrink: 0;"></div>
            <div ${animateWaiting ? 'class="wave-dot"' : ''} style="width: 4px; height: 4px; border-radius: 50%; background: #5f6368; flex-shrink: 0;"></div>
            <div ${animateWaiting ? 'class="wave-dot"' : ''} style="width: 4px; height: 4px; border-radius: 50%; background: #5f6368; flex-shrink: 0;"></div>
          </div>
        ` : ''}
        
        ${showMoreCounter ? `
          <div 
            onclick='window.showAllParticipantsList(${JSON.stringify(formattedEvent)})'
            style="
              width: 40px;
              height: 40px;
              border-radius: 50%;
              background: #dddddd;
              border: 2px solid #e8eaed;
              display: flex;
              align-items: center;
              justify-content: center;
              cursor: pointer;
              flex-shrink: 0;
              font-size: 12px;
              font-weight: 600;
              color: #202124;
            "
            title="View all ${otherParticipantIds.length} participants"
          >
            +${remainingCount}
          </div>
        ` : ''}
      `;
    }
    
    // Reload events in the background
    if (window.loadUserEvents) {
      await window.loadUserEvents();
    }
    
  } catch (err) {
    console.error('Error updating panel:', err);
  }
};

export const startRealtimeStatusUpdates = () => {
  const lastKnownStatus = new Map();
  
  if (window.statusUpdateInterval) {
    clearInterval(window.statusUpdateInterval);
  }
  
window.statusUpdateInterval = setInterval(() => {
    // Get current map bounds - check if map is in valid state
    if (!window.leafletMap || !window.markerClusterGroup) return;
    if (window.isLoggingOut) return; // Skip if logging out
    
    // Extra safety check for map state
    try {
      if (!window.leafletMap._container || !document.body.contains(window.leafletMap._container)) {
        return;
      }
      const bounds = window.leafletMap.getBounds();
    
    // Only update markers in viewport
    document.querySelectorAll('.custom-event-marker').forEach(markerWrapper => {
      // Get marker's coordinates
      const innerDiv = markerWrapper.querySelector('div[data-place-id]') || markerWrapper.firstElementChild;
      const placeId = innerDiv?.dataset?.placeId;
      
      if (!placeId || !window.allEvents) return;
      
const eventsAtPlace = window.allEvents.filter(e => e.placeId === placeId);
    if (eventsAtPlace.length === 0) return;
    
    // ✅ SORT EVENTS BEFORE PICKING FIRST ONE (same as marker creation)
    const ongoing = [];
    const upcoming = [];
    const finished = [];
    
    eventsAtPlace.forEach(e => {
      const s = window.calculateEventStatus(e.startDateTime, e.endDateTime);
      if (s.hasEnded) finished.push(e);
      else if (s.hasStarted && s.endDate) ongoing.push(e);
      else upcoming.push(e);
    });
    
    ongoing.sort((a, b) => {
      const sA = window.calculateEventStatus(a.startDateTime, a.endDateTime);
      const sB = window.calculateEventStatus(b.startDateTime, b.endDateTime);
      return sA.endDate - sB.endDate;
    });
    
    upcoming.sort((a, b) => {
      const sA = window.calculateEventStatus(a.startDateTime, a.endDateTime);
      const sB = window.calculateEventStatus(b.startDateTime, b.endDateTime);
      return sA.startDate - sB.startDate;
    });
    
    finished.sort((a, b) => {
      const sA = window.calculateEventStatus(a.startDateTime, a.endDateTime);
      const sB = window.calculateEventStatus(b.startDateTime, b.endDateTime);
      return sB.endDate - sA.endDate;
    });
    
    const sortedEvents = [...ongoing, ...upcoming, ...finished];
    const event = sortedEvents[0]; // ← Now this is the FIRST event in sorted order
    
    // ✅ CHECK IF MARKER IS IN VIEWPORT
    const markerLatLng = L.latLng(event.latitude, event.longitude);
    if (!bounds.contains(markerLatLng)) {
      return; // Skip - marker is off-screen
    }
    
    // USE MASTER STATUS CALCULATOR
    const status = window.calculateEventStatus(event.startDateTime, event.endDateTime);
      
      const lastStatus = lastKnownStatus.get(placeId);
      if (lastStatus === status.statusText) return; // Skip if unchanged
      
      // Update marker (same as before)
      const newStatusColor = status.statusColor;
      const shouldShowPulse = status.shouldPulse;
      
      const circle = markerWrapper.querySelector('[data-marker-type="first-circle"]');
      const label = markerWrapper.querySelector('[data-marker-type="label"]');
      const pulseOuter = markerWrapper.querySelector('[data-marker-type="first-pulse-outer"]');
      const pulseMiddle = markerWrapper.querySelector('[data-marker-type="first-pulse-middle"]');
      const pulseInner = markerWrapper.querySelector('[data-marker-type="first-pulse"]');
      
      if (circle) {
        circle.style.borderColor = newStatusColor;
        circle.style.setProperty('border-color', newStatusColor, 'important');
      }
      if (label) {
        label.style.borderColor = newStatusColor;
        label.style.setProperty('border-color', newStatusColor, 'important');
      }
      
      if (pulseOuter) {
        pulseOuter.style.background = newStatusColor;
        pulseOuter.style.display = shouldShowPulse ? 'block' : 'none';
      }
      if (pulseMiddle) {
        pulseMiddle.style.background = newStatusColor;
        pulseMiddle.style.display = shouldShowPulse ? 'block' : 'none';
      }
      if (pulseInner) {
        pulseInner.style.background = newStatusColor;
        pulseInner.style.display = shouldShowPulse ? 'block' : 'none';
      }
      
      lastKnownStatus.set(placeId, status.statusText);
    });
    
    // Update profile panel cards (same as before)
    document.querySelectorAll('[data-event-card]').forEach(card => {
      const startDateTime = card.dataset.startDateTime;
      const endDateTime = card.dataset.endDateTime;
      
      if (!startDateTime || !endDateTime) return;
      
      const status = window.calculateEventStatus(startDateTime, endDateTime);
      const cardId = `${startDateTime}_${endDateTime}`;
      const lastStatus = lastKnownStatus.get(cardId);
      
      if (lastStatus === status.statusText) return;
      
      const statusColor = status.statusColor;
      const shouldShowPulse = status.shouldPulse;
      
      card.style.borderColor = statusColor;
      
      const pulseElement = card.querySelector('[data-card-pulse]');
      if (pulseElement) {
        pulseElement.style.borderColor = statusColor;
        pulseElement.style.display = shouldShowPulse ? 'block' : 'none';
      }
      
      lastKnownStatus.set(cardId, status.statusText);
      
    });
    
  }  catch (e) {
      console.warn('Skipping status update - map not ready');
  }},5000); // Every 5 seconds
};

// Set up real-time status updates for event participants
export const setupParticipantStatusUpdates = (participantIds) => {
  const supabaseClient = window.supabase || supabase;
  
  // Clean up existing subscription
  if (window.participantStatusSubscription) {
    supabaseClient.removeChannel(window.participantStatusSubscription);
  }
  
  // Clean up existing interval
  if (window.participantStatusInterval) {
    clearInterval(window.participantStatusInterval);
  }

  // Function to update all participant statuses
  const updateAllStatuses = async () => {
    for (const userId of participantIds) {
      const isOnline = await window.getUserOnlineStatus(userId);
      const statusColor = isOnline ? '#34a853' : '#9e9e9e';
      
      // Update avatar border and status circle
      const avatarImg = document.querySelector(`img[data-user-id="${userId}"]`);
      if (avatarImg) {
        avatarImg.style.border = `2px solid ${statusColor}`;
        
        // Update status circle
        const statusCircle = avatarImg.parentElement?.querySelector('div[style*="position: absolute"]');
        if (statusCircle) {
          statusCircle.style.background = statusColor;
        }
      }
    }
  };
  
  // Poll every 15 seconds to catch offline transitions
  window.participantStatusInterval = setInterval(updateAllStatuses, 15000);
  
  // Real-time subscription for immediate updates
  window.participantStatusSubscription = supabaseClient
    .channel('participant-status')
    .on(
      'postgres_changes',
      {
        event: 'UPDATE',
        schema: 'public',
        table: 'users'
      },
      async (payload) => {
        const userId = payload.new.id;
        
        // Only update if this user is a participant
        if (!participantIds.includes(userId)) return;
        
        const isOnline = await window.getUserOnlineStatus(userId);
        const statusColor = isOnline ? '#34a853' : '#9e9e9e';
        
        // Update avatar border and status circle
        const avatarImg = document.querySelector(`img[data-user-id="${userId}"]`);
        if (avatarImg) {
          avatarImg.style.border = `2px solid ${statusColor}`;
          
          // Update status circle
          const statusCircle = avatarImg.parentElement?.querySelector('div[style*="position: absolute"]');
          if (statusCircle) {
            statusCircle.style.background = statusColor;
          }
        }
      }
    )
    .subscribe();
};

window.setupEventRealtimeUpdates = setupEventRealtimeUpdates;
window.cleanupEventRealtimeUpdates = cleanupEventRealtimeUpdates;
window.updateEventDetailsPanel = updateEventDetailsPanel;
window.startRealtimeStatusUpdates = startRealtimeStatusUpdates;
window.setupParticipantStatusUpdates = setupParticipantStatusUpdates;